import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { FormEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatListItemComponent } from '../../components/chats-list/chats-list-item/chats-list-item.component';
import { ChatsListComponent } from '../../components/chats-list/chats-list.component';
import { ToolGroupComponent } from '../../components/tool-group/tool-group.component';
import { useRequest } from '../../hooks/useRequest';
import {
  AdminTool,
  createToolPreset,
  deleteToolPreset,
  fetchBuiltinTools,
  fetchMcpTools,
  fetchToolPresets,
  ToolPreset,
  updateToolPreset,
} from '../../utils/axios/api';

type ToolGroups = {
  builtin: AdminTool[];
  mcp: AdminTool[];
};

export function AdminToolPresetsPage() {
  const { t } = useTranslation();
  const loadPresetsRequest = useRequest<ToolPreset[]>();
  const loadToolsRequest = useRequest<ToolGroups>();
  const saveRequest = useRequest<ToolPreset>();
  const deleteRequest = useRequest();
  const [presets, setPresets] = useState<ToolPreset[]>([]);
  const [availableTools, setAvailableTools] = useState<ToolGroups>({
    builtin: [],
    mcp: [],
  });
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [selectedTools, setSelectedTools] = useState(() => new Set<string>());
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    void loadPresetsRequest.fetch(() => fetchToolPresets()).then((data) => {
      if (!data) {
        setPresets([]);
        return;
      }
      setPresets(data);
    });

    void loadToolsRequest
      .fetch(async () => {
        const [builtin, mcp] = await Promise.all([
          fetchBuiltinTools(),
          fetchMcpTools(),
        ]);
        return { builtin, mcp };
      })
      .then((data) => {
        if (!data) {
          setAvailableTools({ builtin: [], mcp: [] });
          return;
        }
        setAvailableTools(data);
      });
  }, [loadPresetsRequest, loadToolsRequest]);

  const resetForm = useCallback(() => {
    setSelectedPresetId(null);
    setName('');
    setSelectedTools(new Set());
    setValidationError(null);
    setIsDeleteDialogOpen(false);
  }, []);

  const handleNewPreset = useCallback(() => {
    resetForm();
  }, [resetForm]);

  const handleSelectPreset = useCallback((preset: ToolPreset) => {
    setSelectedPresetId(preset.id);
    setName(preset.name);
    setSelectedTools(new Set(preset.tools.map((tool) => tool.name)));
    setValidationError(null);
    setIsDeleteDialogOpen(false);
  }, []);

  const handleToggleTool = useCallback((toolName: string) => {
    setSelectedTools((prev) => {
      const next = new Set(prev);
      if (next.has(toolName)) {
        next.delete(toolName);
      } else {
        next.add(toolName);
      }
      return next;
    });
  }, []);

  const handleDelete = useCallback(async () => {
    if (!selectedPresetId) {
      return;
    }

    setValidationError(null);

    const result = await deleteRequest.fetch(async () => {
      await deleteToolPreset(selectedPresetId);
    });
    if (result === null) {
      setIsDeleteDialogOpen(false);
      return;
    }
    setPresets((prev) => prev.filter((item) => item.id !== selectedPresetId));
    resetForm();
  }, [deleteRequest, resetForm, selectedPresetId]);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const trimmedName = name.trim();

      if (!trimmedName) {
        setValidationError(t('admin.toolPresets.errors.required'));
        return;
      }

      setValidationError(null);

      if (selectedPresetId) {
        const updated = await saveRequest.fetch(() =>
          updateToolPreset(selectedPresetId, {
            name: trimmedName,
            tools: Array.from(selectedTools),
          }),
        );
        if (!updated) {
          return;
        }

        setPresets((prev) =>
          prev.map((item) => (item.id === updated.id ? updated : item)),
        );
        setName(updated.name);
        setSelectedTools(new Set(updated.tools.map((tool) => tool.name)));
      } else {
        const created = await saveRequest.fetch(() =>
          createToolPreset({
            name: trimmedName,
            tools: Array.from(selectedTools),
          }),
        );
        if (!created) {
          return;
        }

        setPresets((prev) => [created, ...prev]);
        setSelectedPresetId(created.id);
        setName(created.name);
        setSelectedTools(new Set(created.tools.map((tool) => tool.name)));
      }
    },
    [name, saveRequest, selectedPresetId, selectedTools, t],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100%',
      }}
    >
      <Box
        sx={{
          width: 280,
          flexShrink: 0,
          borderRight: 1,
          borderColor: 'divider',
        }}
      >
        <ChatsListComponent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Typography variant="h6">{t('admin.toolPresets.title')}</Typography>
            <Tooltip title={t('admin.toolPresets.new')}>
              <IconButton
                size="small"
                aria-label={t('admin.toolPresets.new')}
                onClick={handleNewPreset}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {loadPresetsRequest.isLoading ? (
            <CircularProgress size={24} />
          ) : presets.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('admin.toolPresets.empty')}
            </Typography>
          ) : (
            presets.map((preset) => (
              <ChatListItemComponent
                key={preset.id}
                chat={{
                  id: preset.id,
                  name: preset.name,
                  userId: '',
                  presetId: null,
                  createdAt: preset.createdAt,
                  updatedAt: preset.updatedAt,
                }}
                selected={preset.id === selectedPresetId}
                onClick={() => {
                  handleSelectPreset(preset);
                }}
              />
            ))
          )}
        </ChatsListComponent>
      </Box>
      <Box
        component="form"
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          flex: 1,
          minWidth: 0,
          p: 2,
        }}
      >
        <Typography variant="h6">
          {selectedPresetId
            ? t('admin.toolPresets.edit')
            : t('admin.toolPresets.create')}
        </Typography>
        <TextField
          label={t('admin.toolPresets.name')}
          value={name}
          onChange={(event) => {
            setName(event.target.value);
          }}
          fullWidth
          required
          disabled={saveRequest.isLoading}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <Typography variant="subtitle1">
            {t('admin.toolPresets.tools')}
          </Typography>
          {loadToolsRequest.exception ? (
            <Typography variant="body2" color="text.secondary">
              {t(`exceptions.${loadToolsRequest.exception}`)}
            </Typography>
          ) : (
            <>
              <ToolGroupComponent
                title={t('admin.toolPresets.builtin')}
                tools={availableTools.builtin}
                emptyLabel={t('admin.toolPresets.toolsEmpty')}
                selectedTools={selectedTools}
                disabled={saveRequest.isLoading}
                onToggle={handleToggleTool}
              />
              <ToolGroupComponent
                title={t('admin.toolPresets.mcp')}
                tools={availableTools.mcp}
                emptyLabel={t('admin.toolPresets.toolsEmpty')}
                selectedTools={selectedTools}
                disabled={saveRequest.isLoading}
                onToggle={handleToggleTool}
              />
            </>
          )}
        </Box>
        {validationError ??
        (deleteRequest.exception ?? saveRequest.exception) ? (
          <Typography variant="body2" color="error">
            {validationError ??
              t(
                `exceptions.${deleteRequest.exception ?? saveRequest.exception}`,
              )}
          </Typography>
        ) : null}
        <Box
          sx={{
            display: 'flex',
            justifyContent: selectedPresetId ? 'space-between' : 'flex-end',
            gap: 1,
            mt: 'auto',
          }}
        >
          {selectedPresetId ? (
            <Button
              color="error"
              variant="outlined"
              disabled={saveRequest.isLoading || deleteRequest.isLoading}
              onClick={() => setIsDeleteDialogOpen(true)}
            >
              {t('admin.toolPresets.delete.button')}
            </Button>
          ) : null}
          <Button
            type="submit"
            variant="contained"
            disabled={saveRequest.isLoading || deleteRequest.isLoading}
          >
            {saveRequest.isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : selectedPresetId ? (
              t('admin.toolPresets.save')
            ) : (
              t('admin.toolPresets.createSubmit')
            )}
          </Button>
        </Box>
        <Dialog
          open={isDeleteDialogOpen}
          onClose={() => {
            if (!deleteRequest.isLoading) {
              setIsDeleteDialogOpen(false);
            }
          }}
        >
          <DialogTitle>{t('admin.toolPresets.delete.title')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('admin.toolPresets.delete.confirm', { name })}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={deleteRequest.isLoading}
            >
              {t('admin.toolPresets.delete.cancel')}
            </Button>
            <Button
              color="error"
              variant="contained"
              disabled={deleteRequest.isLoading}
              onClick={() => {
                void handleDelete();
              }}
            >
              {deleteRequest.isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                t('admin.toolPresets.delete.button')
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}
